import type { SessionState } from './types'

/**
 * The session state machine.
 *
 * It is written as an explicit table rather than a pile of `if` statements
 * because the failure mode this guards against is real and common in voice
 * UIs: a late event from a cancelled turn arrives after the user has already
 * moved on, and the orb ends up showing "listening" while the microphone is
 * shut. Illegal transitions are rejected here, counted, and surfaced in
 * diagnostics instead of silently corrupting the display.
 */

export type SessionEvent =
  | 'START'
  | 'REQUEST_PERMISSION'
  | 'PERMISSION_GRANTED'
  | 'PERMISSION_DENIED'
  | 'CONNECTED'
  | 'CONNECTION_LOST'
  | 'RECONNECTED'
  | 'LISTEN_START'
  | 'USER_SPEAK_START'
  | 'USER_SPEAK_END'
  | 'PROCESSING_START'
  | 'AGENT_SPEAK_START'
  | 'AGENT_SPEAK_END'
  | 'INTERRUPT'
  | 'END'
  | 'ERROR'
  | 'RETRY'
  | 'RESET'

type TransitionTable = {
  readonly [S in SessionState]: Partial<Record<SessionEvent, SessionState>>
}

/**
 * `END`, `ERROR` and `RESET` are legal from nearly everywhere, so they are
 * merged in below rather than repeated on every row.
 */
const UNIVERSAL: Partial<Record<SessionEvent, SessionState>> = {
  END: 'ended',
  ERROR: 'error',
  RESET: 'idle',
}

/**
 * Note the order: START leads to `connecting`, not to `requesting_permission`.
 *
 * Namu speaks before it asks to listen. That is a deliberate sequence and it
 * is worth defending. Permission prompts fire once and are remembered forever,
 * and a request made before the person has any idea what the app does is the
 * one most likely to be refused. Speaking first costs nothing, needs no
 * microphone, and is the clearest possible explanation of why the microphone
 * is about to be asked for.
 */
const TABLE: TransitionTable = {
  idle: {
    START: 'connecting',
  },
  requesting_permission: {
    PERMISSION_GRANTED: 'listening',
    PERMISSION_DENIED: 'error',
    AGENT_SPEAK_START: 'model_speaking',
  },
  connecting: {
    CONNECTED: 'listening',
    AGENT_SPEAK_START: 'model_speaking',
    REQUEST_PERMISSION: 'requesting_permission',
    CONNECTION_LOST: 'reconnecting',
  },
  listening: {
    USER_SPEAK_START: 'user_speaking',
    AGENT_SPEAK_START: 'model_speaking',
    PROCESSING_START: 'processing',
    REQUEST_PERMISSION: 'requesting_permission',
    CONNECTION_LOST: 'reconnecting',
    LISTEN_START: 'listening',
  },
  user_speaking: {
    USER_SPEAK_END: 'processing',
    PROCESSING_START: 'processing',
    LISTEN_START: 'listening',
    CONNECTION_LOST: 'reconnecting',
  },
  processing: {
    AGENT_SPEAK_START: 'model_speaking',
    LISTEN_START: 'listening',
    CONNECTION_LOST: 'reconnecting',
  },
  model_speaking: {
    AGENT_SPEAK_END: 'listening',
    INTERRUPT: 'interrupted',
    USER_SPEAK_START: 'user_speaking',
    LISTEN_START: 'listening',
    PROCESSING_START: 'processing',
    // The microphone is asked for after the greeting, so this transition is
    // the normal path through a first run, not an edge case.
    REQUEST_PERMISSION: 'requesting_permission',
    CONNECTION_LOST: 'reconnecting',
  },
  interrupted: {
    USER_SPEAK_START: 'user_speaking',
    LISTEN_START: 'listening',
    PROCESSING_START: 'processing',
    AGENT_SPEAK_START: 'model_speaking',
  },
  reconnecting: {
    RECONNECTED: 'listening',
    CONNECTED: 'listening',
    AGENT_SPEAK_START: 'model_speaking',
  },
  ended: {
    START: 'connecting',
  },
  error: {
    RETRY: 'idle',
    START: 'connecting',
    // Recovering from a refused microphone means asking again, which the
    // browser only allows from a fresh gesture.
    REQUEST_PERMISSION: 'requesting_permission',
  },
}

/** States where the session is doing work and should hold the screen awake. */
export const ACTIVE_STATES: ReadonlySet<SessionState> = new Set<SessionState>([
  'connecting',
  'listening',
  'user_speaking',
  'processing',
  'model_speaking',
  'interrupted',
  'reconnecting',
])

/** States where the microphone is, or should be, capturing. */
export const CAPTURING_STATES: ReadonlySet<SessionState> = new Set<SessionState>([
  'listening',
  'user_speaking',
])

export const INITIAL_STATE: SessionState = 'idle'

/**
 * Returns the state this event leads to, or `null` if the event does not apply.
 * Pure, so the whole machine is testable without a browser.
 */
export function nextState(state: SessionState, event: SessionEvent): SessionState | null {
  const row = TABLE[state]
  // `ended` deliberately ignores a second END, and `error` ignores ERROR, so
  // the row wins over the universal fallbacks where it defines the event.
  const target = row[event] ?? UNIVERSAL[event]
  if (target === undefined) return null
  if (target === state && event !== 'LISTEN_START') return null
  return target
}

export function canTransition(state: SessionState, event: SessionEvent): boolean {
  return nextState(state, event) !== null
}

export interface RejectedTransition {
  from: SessionState
  event: SessionEvent
  at: number
}

/**
 * Stateful wrapper that keeps the current state and a bounded log of rejected
 * events. The rejection log is the interesting part: a burst of rejections is
 * the signature of a transport that is emitting events out of order, and it is
 * the first thing to look at when the orb and the microphone disagree.
 */
export class SessionMachine {
  private state: SessionState = INITIAL_STATE
  private readonly rejected: RejectedTransition[] = []
  private readonly maxRejected: number

  constructor(options: { initial?: SessionState; maxRejected?: number } = {}) {
    this.state = options.initial ?? INITIAL_STATE
    this.maxRejected = options.maxRejected ?? 50
  }

  get current(): SessionState {
    return this.state
  }

  get rejectedTransitions(): readonly RejectedTransition[] {
    return this.rejected
  }

  /** Applies the event. Returns the new state, or null if it was rejected. */
  send(event: SessionEvent, now: number = Date.now()): SessionState | null {
    const target = nextState(this.state, event)
    if (target === null) {
      this.rejected.push({ from: this.state, event, at: now })
      if (this.rejected.length > this.maxRejected) this.rejected.shift()
      return null
    }
    this.state = target
    return target
  }

  reset(): void {
    this.state = INITIAL_STATE
    this.rejected.length = 0
  }
}
